/**
 * The signed-in frame: the dark rail (signal path + navigation) and the top
 * bar with the site switcher. On narrow screens the rail becomes a drawer.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { can, ROLE_LABELS, type Capability } from './auth/roles';
import { useSession } from './auth/session';
import { Link, useRouter } from './router';
import { stepsFor } from './setup/steps';
import { useProgress } from './setup/progress';
import { useSites } from './state/sites';
import { useLastSeen } from './state/lastSeen';
import { applyTheme, readTheme, type ThemeChoice } from './theme';
import { PENDING_TITLE, PendingExplanation, PlanExplanation, planLabel } from './org/status';
import { Popover } from './ui/popover';
import { useOpsPendingCount } from './screens/Ops';
import { NEW_SITE_PATH, requestNewSiteFocus } from './state/newSite';
import { ClaimBanner } from './screens/Claim';

function BrandMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 32 32" aria-hidden="true">
      <defs>
        <linearGradient id="bm" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--brand-a)" />
          <stop offset="1" stopColor="var(--brand-b)" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="currentColor" opacity="0.08" />
      <path d="M9 22V10h10M9 16h7" stroke="url(#bm)" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="23" cy="21" r="2.6" fill="url(#bm)" />
    </svg>
  );
}

interface NavItem {
  to: string;
  label: string;
  capability: Capability;
}

const OPERATE: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', capability: 'funnel' },
  { to: '/pages', label: 'Your page', capability: 'hostedPage' },
  { to: '/leads', label: 'Leads', capability: 'leads' },
  { to: '/billing', label: 'Billing', capability: 'billing' },
];
const CONFIGURE: NavItem[] = [
  { to: '/sites', label: 'Sites & keys', capability: 'sites' },
  { to: '/config', label: 'Widget settings', capability: 'editConfig' },
  { to: '/destinations', label: 'Destinations', capability: 'destinations' },
  { to: '/contractors', label: 'Contractors', capability: 'roster' },
  { to: '/routing', label: 'Routing rules', capability: 'routing' },
];
const ACCOUNT: NavItem[] = [
  { to: '/team', label: 'Team & roles', capability: 'members' },
  { to: '/agent', label: 'AI agent access', capability: 'agentTokens' },
  { to: '/audit', label: 'Audit log', capability: 'audit' },
  { to: '/settings', label: 'Organisation', capability: 'readOrg' },
  { to: '/account', label: 'Your account', capability: 'readOrg' },
];

/**
 * FinderlyFix admin (migration 076): only for a person `whoami` says holds an
 * operator grant. The server's `/partner/ops/*` checks the grant itself.
 */
function OpsNav({ path }: { path: string }) {
  const pending = useOpsPendingCount(true);
  const here = (to: string, exact: boolean) => (exact ? path === to : path === to || path.startsWith(`${to}/`));
  return (
    <nav className="rail-section" aria-label="FinderlyFix admin">
      <div className="rail-heading">FinderlyFix admin</div>
      <Link to="/ops" className="nav" aria-current={here('/ops', true) ? 'page' : undefined}>
        Pending approvals
        {pending ? (
          <span className="count" data-testid="ops-pending-count">
            {pending}
            <span className="visually-hidden"> waiting</span>
          </span>
        ) : null}
      </Link>
      <Link to="/ops/partners" className="nav" aria-current={here('/ops/partners', false) ? 'page' : undefined}>
        All partners
      </Link>
    </nav>
  );
}

function NavLink({ item, path }: { item: NavItem; path: string }) {
  const active = path === item.to || path.startsWith(`${item.to}/`);
  return (
    <Link to={item.to} className="nav" aria-current={active ? 'page' : undefined}>
      {item.label}
    </Link>
  );
}

/** The host a site's live key was last seen on (install health), or null. */
/** The websites a site's live key has loaded on, most recently seen first. */
function useSiteHosts(): (liveKey: string) => string[] {
  const { health } = useLastSeen();
  return (liveKey) =>
    (health?.origins ?? [])
      .filter((o) => o.site_key === liveKey && o.last_impression_at)
      .sort((a, b) => String(b.last_impression_at).localeCompare(String(a.last_impression_at)))
      .map((o) => {
        try {
          return new URL(o.origin).host;
        } catch {
          return o.origin.replace(/^https?:\/\//, '');
        }
      })
      .filter((host, i, all) => all.indexOf(host) === i);
}

/** "summit.example +1": the most recent website, and how many more the key works on. */
export function hostsSummary(hosts: string[]): string | null {
  if (!hosts.length) return null;
  return hosts.length === 1 ? hosts[0]! : `${hosts[0]} +${hosts.length - 1}`;
}

/**
 * "+ New site" beside the top bar's site (owners and admins): deep-links to
 * Sites & keys with the "New site" field focused (state/newSite.ts).
 */
export function NewSiteLink() {
  const { role } = useSession();
  const { path } = useRouter();
  if (!can(role, 'addSite')) return null;
  return (
    <Link
      to={NEW_SITE_PATH}
      className="small site-new"
      onClick={(e) => {
        // Already on Sites & keys: the router ignores a hash-only change, so focus directly.
        if (path !== '/sites' || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        requestNewSiteFocus();
      }}
    >
      <span aria-hidden="true">+</span> New site
    </Link>
  );
}

/**
 * The top bar's site. One site: plain text ("Summit · summit.25x.codes") and
 * a link to Sites & keys — a one-option dropdown read as a control that did
 * nothing (owner feedback, 2026-09-27). Several: a select that is always
 * labelled, phones included, each option naming the site and its host.
 * Switching asks first when there are unsaved edits.
 */
export function SiteSwitcher() {
  const { pairs, current, select } = useSites();
  const { role } = useSession();
  const { confirmLeave } = useRouter();
  const hostOf = useSiteHosts();
  if (!pairs.length) return null;
  const describe = (label: string, key: string) => {
    const hosts = hostOf(key);
    return hosts.length ? `${label} · ${hosts.join(', ')}` : label;
  };
  if (pairs.length === 1 && current) {
    return (
      <span className="site-switch site-single" data-testid="site-single">
        <span className="label">Site</span>
        <span className="site-name" title={describe(current.live.label, current.live.site_key)}>
          <span className="site-label">{current.live.label}</span>
          {hostsSummary(hostOf(current.live.site_key)) ? (
            <span className="site-host">
              <span className="site-sep">{"\u00a0·\u00a0"}</span>
              <span aria-hidden="true">{hostsSummary(hostOf(current.live.site_key))}</span>
              <span className="visually-hidden">{hostOf(current.live.site_key).join(', ')}</span>
            </span>
          ) : null}
        </span>
        {can(role, 'sites') ? (
          <Link to="/sites" className="small site-manage">
            Manage<span className="visually-hidden"> sites and keys</span>
          </Link>
        ) : null}
        <NewSiteLink />
      </span>
    );
  }
  return (
    <>
    <label className="site-switch" htmlFor="topbar-site">
      <span className="label">Site</span>
      <select
        id="topbar-site"
        className="input"
        style={{ minHeight: 32, padding: '3px 8px' }}
        value={current?.live.site_key ?? ''}
        onChange={(e) => {
          const key = e.target.value;
          // Switching sites reloads the settings: unsaved edits would be dropped silently.
          confirmLeave(() => select(key));
        }}
      >
        {pairs.map((p) => (
          <option key={p.live.site_key} value={p.live.site_key}>
            {describe(p.live.label, p.live.site_key)}
          </option>
        ))}
      </select>
    </label>
    <NewSiteLink />
    </>
  );
}

export function Shell({ children, onboarding }: { children: ReactNode; onboarding?: boolean }) {
  const { org, role, whoami, signOut, memberships, selectOrg } = useSession();
  const { statusOf } = useProgress();
  const { path, confirmLeave } = useRouter();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(readTheme());
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setOpen(false);
    // Move focus to the new page's heading so screen readers announce it.
    const h = document.getElementById('page-title');
    if (h) h.focus({ preventScroll: true });
  }, [path]);

  const steps = stepsFor(role);
  const section = (title: string, items: NavItem[]) => {
    const visible = items.filter((i) => can(role, i.capability));
    if (!visible.length) return null;
    return (
      <nav className="rail-section" aria-label={title}>
        <div className="rail-heading">{title}</div>
        {visible.map((i) => (
          <NavLink key={i.to} item={i} path={path} />
        ))}
      </nav>
    );
  };

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <aside className="rail" data-open={open} aria-label="Portal navigation">
        <Link to="/" className="brand">
          <BrandMark />
          <span>
            FinderlyFix
            <small>Partners</small>
          </span>
        </Link>
        {steps.length ? (
          <nav className="rail-section" aria-label="Setup">
            <div className="rail-heading">Setup</div>
            <ol className="path">
              {steps.map((s) => {
                const to = `/setup/${s.slug}`;
                const here = path === to;
                const status = statusOf(s);
                const done = status === 'done';
                return (
                  <li key={s.slug} className={`${done ? 'done' : ''} ${here ? 'current' : ''}`}>
                    {/* Done is a check mark, not only a filled node, and is said in words. */}
                    <span className="node" aria-hidden="true">
                      {done ? '✓' : ''}
                    </span>
                    <Link to={to} className="nav" aria-current={here ? 'step' : undefined}>
                      <span>{s.title}</span>
                      {done ? <span className="visually-hidden">(done)</span> : null}
                      {status === 'optional' ? <span className="later">Optional</span> : null}
                    </Link>
                  </li>
                );
              })}
            </ol>
          </nav>
        ) : null}
        {section('Operate', OPERATE)}
        {section('Configure', CONFIGURE)}
        {section('Account', ACCOUNT)}
        {whoami?.is_operator ? <OpsNav path={path} /> : null}
        <div className="rail-foot">
          <Link to="/account" className="who" title={`${whoami?.email ?? ''} · Your account`}>
            {whoami?.email}
          </Link>
          <span>
            {org?.display_name} · {role ? ROLE_LABELS[role] : ''}
          </span>
          {memberships.length > 1 ? (
            <label className="small" htmlFor="rail-org-switch">
              <span className="visually-hidden">Switch organisation</span>
              <select id="rail-org-switch" value={org?.id ?? ''} onChange={(e) => void selectOrg(e.target.value)}>
                {memberships.map((m) => (
                  <option key={m.partner_id} value={m.partner_id}>
                    {m.display_name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div className="row" style={{ gap: 6 }}>
            <label htmlFor="rail-theme">
              <span className="visually-hidden">Colour theme</span>
              <select
                id="rail-theme"
                value={theme}
                onChange={(e) => {
                  const v = e.target.value as ThemeChoice;
                  setTheme(v);
                  applyTheme(v);
                }}
              >
                <option value="auto">System theme</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>
            <button type="button" onClick={() => confirmLeave(() => void signOut())}>
              Sign out
            </button>
          </div>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button type="button" className="btn icon-btn menu-btn" aria-label="Open navigation" aria-expanded={open} onClick={() => setOpen(!open)}>
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path d="M2 4h14M2 9h14M2 14h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          <SiteSwitcher />
          <span className="grow" />
          <span className="topbar-badges">
            {org?.status === 'pending' ? (
              <Popover
                className="tag warn"
                title={PENDING_TITLE}
                buttonLabel={PENDING_TITLE}
                label={
                  <>
                    <span className="hide-sm">{PENDING_TITLE}</span>
                    <span className="show-sm" aria-hidden="true">Pending</span>
                  </>
                }
              >
                <PendingExplanation />
              </Popover>
            ) : null}
            {org?.plan ? (
              <Popover className="tag accent" title={`${planLabel(org.plan)} plan`} buttonLabel={`${planLabel(org.plan)} plan`} label={
                <>
                  <span className="hide-sm">{planLabel(org.plan)} plan</span>
                  <span className="show-sm" aria-hidden="true">{planLabel(org.plan)}</span>
                </>
              }>
                <PlanExplanation plan={org.plan} />
              </Popover>
            ) : null}
          </span>
        </header>
        <main id="main" ref={mainRef} className={`content${onboarding ? ' onboard' : ''}`} tabIndex={-1}>
          {path === '/claim' ? null : <ClaimBanner />}
          {children}
        </main>
      </div>
    </div>
  );
}
